/**
 * Compact multi-step wizard. Shows only the ACTIVE step in a single-line header
 * (Step N of M · Title) with a thin progress bar — avoids the tall, wrapping
 * stepper. Same step contract as the previous MultiStepForm usage:
 * { title, description?, content, validate? }. validate() runs on Next/Complete;
 * returning false keeps the user on the step (the step content shows what's missing).
 */

import { ReactNode, useState } from 'react';
import { Stack, Group, Text, Button, Badge, Progress } from '../../../../components/ui';
import { Card } from '../../../../components/ui/data-display';

export interface WizardStep {
  title: string;
  description?: string;
  content: ReactNode;
  validate?: () => boolean | Promise<boolean>;
}

export function CompactWizard({
  steps,
  onComplete,
  completeLabel = 'Finish',
  loading,
}: {
  steps: WizardStep[];
  onComplete: () => void | Promise<void>;
  completeLabel?: string;
  loading?: boolean;
}) {
  const [index, setIndex] = useState(0);
  const [busy, setBusy] = useState(false);
  const step = steps[index];
  const isLast = index === steps.length - 1;

  const next = async () => {
    const ok = (await step.validate?.()) ?? true;
    if (ok && !isLast) setIndex((i) => i + 1);
  };
  const complete = async () => {
    const ok = (await step.validate?.()) ?? true;
    if (!ok) return;
    setBusy(true);
    try {
      await onComplete();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack gap="md">
      {/* Single-line indicator */}
      <Card withBorder padding="sm">
        <Group justify="space-between" wrap="nowrap" gap="sm">
          <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
            <Badge variant="light" radius="sm" style={{ flexShrink: 0 }}>Step {index + 1} of {steps.length}</Badge>
            <Text fw={600} truncate>{step.title}</Text>
            {step.description && <Text size="sm" c="dimmed" truncate visibleFrom="md">— {step.description}</Text>}
          </Group>
        </Group>
        <Progress value={((index + 1) / steps.length) * 100} size="xs" mt="xs" radius="xl" />
      </Card>

      {/* Active step content */}
      {step.content}

      {/* Footer */}
      <Group justify="space-between">
        <Button variant="default" onClick={() => setIndex((i) => Math.max(0, i - 1))} disabled={index === 0 || busy}>
          Back
        </Button>
        {isLast ? (
          <Button onClick={complete} loading={loading || busy} gradientVariant="primary">{completeLabel}</Button>
        ) : (
          <Button onClick={next} disabled={busy} gradientVariant="primary">Next</Button>
        )}
      </Group>
    </Stack>
  );
}
